import React from "react";
import { createPortal } from "react-dom";
import { Box, ButtonBase } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import {
  DndContext, DragOverlay, KeyboardSensor, MeasuringStrategy, PointerSensor,
  closestCorners, defaultDropAnimationSideEffects, pointerWithin, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { T } from "utils/backrrTokens";
import { reorder } from "../layout/catalog";

const HANDLE = "data-dnd-handle";
const GRID = "data-dnd-grid";
const ITEM = "data-dnd-id";
const SKIP = `[${HANDLE}], a, button, input, textarea, select, label, [role="button"], [role="link"], [role="tab"], [data-no-dnd]`;

// Drag starts from any plain surface; links, buttons and inputs keep their clicks.
class SurfacePointerSensor extends PointerSensor {
  static activators = [{
    eventName: "onPointerDown",
    handler: ({ nativeEvent: e }, { onActivation }) => {
      if (!e.isPrimary || e.button !== 0) return false;
      const hit = e.target.closest?.(SKIP);
      if (hit && !hit.hasAttribute(HANDLE)) return false;
      onActivation?.({ event: e });
      return true;
    },
  }];
}

// Pointer: only what is under the pointer, so gaps never reshuffle. Keyboard: the slot the arrow
// key moved to, never the item itself (a wide item's own center would otherwise always win).
const collide = (args) => {
  if (args.pointerCoordinates) return pointerWithin(args);
  const start = args.active.rect.current.initial;
  const now = args.collisionRect;
  if (!start || (Math.abs(start.top - now.top) < 1 && Math.abs(start.left - now.left) < 1)) return [{ id: args.active.id }];
  return closestCorners({ ...args, droppableContainers: args.droppableContainers.filter((c) => c.id !== args.active.id) });
};
const keepInPlace = () => null;
const GridIdContext = React.createContext(null);
const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const dropAnimation = {
  duration: 220,
  easing: "cubic-bezier(.2, 0, 0, 1)",
  sideEffects: defaultDropAnimationSideEffects({ styles: { active: { opacity: "0" } } }),
};

// The dragged copy: a snapshot of the real element, so charts and counters are not re-run.
const Snapshot = ({ node }) => {
  const ref = React.useRef(null);
  React.useLayoutEffect(() => {
    if (!ref.current || !node) return undefined;
    const copy = node.cloneNode(true);
    [copy, ...copy.querySelectorAll(`[${GRID}], [${ITEM}]`)].forEach((el) => { el.removeAttribute(GRID); el.removeAttribute(ITEM); });
    ref.current.appendChild(copy);
    return () => copy.remove();
  }, [node]);
  return (
    <Box
      ref={ref}
      aria-hidden
      sx={{
        height: "100%",
        cursor: "grabbing",
        transform: "scale(1.015)",
        filter: "drop-shadow(0 12px 28px rgba(16,24,40,0.16))",
        "& > *": { height: "100%" },
        // The source may already wear its placeholder look when copied; the copy shows the real thing.
        "& > *::after": { display: "none !important" },
        "& > * > .dnd-body": { visibility: "visible !important" },
        "& *": { animation: "none !important", transition: "none !important" },
      }}
    />
  );
};

/**
 * A sortable grid whose items reflow live while one is dragged: the item lifts, a placeholder holds its
 * slot, neighbours glide aside, Esc restores the start order. `order` is the full list (hidden keys keep
 * their place), `ids` the visible ones.
 */
export const DragGrid = ({ ids, order, labels = {}, onOrderChange, children }) => {
  const gridId = React.useId();
  const root = React.useRef(null);
  const start = React.useRef(null);
  const lastOver = React.useRef(null);
  const rects = React.useRef(new Map());
  const [active, setActive] = React.useState(null); // { id, node }
  const name = (id) => labels[id] || id;

  const sensors = useSensors(
    useSensor(SurfacePointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const moveTo = (activeId, overId) => {
    const next = reorder(order, order.indexOf(activeId), order.indexOf(overId));
    if (next !== order) onOrderChange(next);
  };

  // FLIP: items that changed slot during a drag slide from where they were.
  const idsKey = ids.join("|");
  React.useLayoutEffect(() => {
    const els = root.current ? root.current.querySelectorAll(`[${GRID}="${gridId}"]`) : [];
    const next = new Map();
    els.forEach((el) => {
      const id = el.getAttribute(ITEM);
      const r = el.getBoundingClientRect();
      next.set(id, r);
      const prev = rects.current.get(id);
      if (!active || !prev || id === active.id || reducedMotion()) return;
      const dx = prev.left - r.left;
      const dy = prev.top - r.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }], { duration: 220, easing: "cubic-bezier(.2, 0, 0, 1)" });
    });
    rects.current = next;
  }, [idsKey, active, gridId]);

  const end = () => { setActive(null); start.current = null; lastOver.current = null; };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collide}
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      onDragStart={({ active: a }) => {
        start.current = order;
        lastOver.current = null;
        const node = root.current?.querySelector(`[${GRID}="${gridId}"][${ITEM}="${CSS.escape(String(a.id))}"]`);
        setActive({ id: a.id, node });
      }}
      onDragOver={({ active: a, over }) => {
        // Swap once per new target; without this, two unequal items would flip back and forth.
        if (!over || over.id === a.id) { lastOver.current = null; return; }
        if (over.id === lastOver.current) return;
        lastOver.current = over.id;
        moveTo(a.id, over.id);
      }}
      onDragEnd={end}
      onDragCancel={() => {
        if (start.current && start.current !== order) onOrderChange(start.current);
        end();
      }}
      accessibility={{
        screenReaderInstructions: { draggable: "To move this, press space. Use the arrow keys to choose a place, space again to drop it, or escape to cancel." },
        announcements: {
          onDragStart: ({ active: a }) => `Picked up ${name(a.id)}.`,
          onDragOver: ({ active: a, over }) => (over ? `${name(a.id)} moved to where ${name(over.id)} was.` : `${name(a.id)} is not over a slot.`),
          onDragEnd: ({ active: a }) => `${name(a.id)} dropped.`,
          onDragCancel: ({ active: a }) => `Moving ${name(a.id)} cancelled; it is back in place.`,
        },
      }}
    >
      <SortableContext items={ids} strategy={keepInPlace}>
        <GridIdContext.Provider value={gridId}>
          <div ref={root} style={{ display: "contents" }}>{children}</div>
        </GridIdContext.Provider>
      </SortableContext>
      {typeof document !== "undefined" && createPortal(
        <DragOverlay zIndex={1600} dropAnimation={dropAnimation}>
          {active ? <Snapshot node={active.node} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};


/**
 * One draggable item. Grab it anywhere on its plain surface; `handle` adds a grip that also takes keyboard
 * focus. While dragged, its slot shows a dashed placeholder of the same size.
 */
export const DragItem = ({ id, label, handle = false, radius = "16px", children }) => {
  const gridId = React.useContext(GridIdContext);
  const { setNodeRef, setActivatorNodeRef, listeners, attributes, isDragging } = useSortable({ id });
  return (
    <Box
      ref={setNodeRef}
      {...{ [GRID]: gridId, [ITEM]: id }}
      onPointerDown={listeners?.onPointerDown}
      sx={{
        position: "relative",
        height: "100%",
        borderRadius: radius,
        cursor: "grab",
        touchAction: "manipulation",
        // A one-column grid so the card inside stretches to the row: every card matches the
        // height of the card beside it. minmax(0, 1fr) keeps wide charts from forcing overflow.
        "& > .dnd-body": { height: "100%", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", visibility: isDragging ? "hidden" : "visible" },
        "&::after": isDragging ? {
          content: '""', position: "absolute", inset: 0, borderRadius: radius, pointerEvents: "none",
          border: `2px dashed ${T.accent}`, background: "rgba(105, 56, 239, 0.05)",
        } : undefined,
        "&:hover > .dnd-grip, & > .dnd-grip:focus-visible": { opacity: 1 },
      }}
    >
      {handle && (
        <ButtonBase
          ref={setActivatorNodeRef}
          {...attributes}
          onKeyDown={listeners?.onKeyDown}
          {...{ [HANDLE]: "" }}
          className="dnd-grip"
          aria-label={`Drag ${label} to rearrange`}
          sx={{
            position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 2,
            width: 40, height: 18, borderRadius: "999px", background: "#fff", border: `1px solid ${T.border}`,
            boxShadow: "0 1px 2px rgba(16,24,40,.06)", color: T.inkSubtle, cursor: "grab", touchAction: "none",
            opacity: 0, transition: "opacity .15s",
            "&:focus-visible": { outline: `2px solid ${T.accent}`, opacity: 1 },
            "@media (hover: none)": { opacity: 1 },
          }}
        >
          <DragIndicator sx={{ fontSize: 15, transform: "rotate(90deg)" }} />
        </ButtonBase>
      )}
      <div className="dnd-body">{children}</div>
    </Box>
  );
};

// How many of a widget's cards are switched on — what a tile row balances over.
export const shownCount = (view, widget, rows) => (rows || []).filter((r) => view.item(widget, r.key)).length;

// Props for a widget's own card grid: full order from the saved layout, visible cards as the drag targets.
export const cardGrid = (view, widget, rows, onItemOrderChange) => {
  const order = view.sortItems(widget, rows).map((r) => r.key);
  return {
    ids: order.filter((k) => view.item(widget, k)),
    order,
    labels: Object.fromEntries((rows || []).map((r) => [r.key, r.label ?? r.title ?? r.key])),
    onOrderChange: (next) => onItemOrderChange(widget, next),
  };
};
